import { doc, setDoc } from "firebase/firestore";
import {
  getDownloadURL,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import { db, storage } from "../../Firebase/Firebase.utils";

// Primary-photo helper for masterlist buildings.
//
// This module writes to the SAME Storage path and Firestore collection
// that Raven's Commercial Leasing > Masterlist photo gallery uses:
//
//   Storage:   buildingPhotos/<buildingId>/<timestamp>_<sanitized-name>
//   Firestore: buildingPhotos/<buildingId>_<timestamp>_0
//              = { url, fileName, fileSize, fileType, buildingId,
//                  uploadedOn, uploadedBy, createdAt }
//
// Cross-app effect: a photo uploaded here shows up in Raven's Photos
// tab for the same building, and vice versa — as long as ops sets it
// as the primary in whichever app they're using.
//
// We do NOT delete previous photos on "change" or "remove" — Raven's
// model is additive (photos are a gallery, and clearing the primary
// doesn't imply the photo is gone). The CMS only owns `primaryPhotoUrl`
// on the masterlist doc; whether the underlying photo record stays or
// is deleted is a Raven-side decision.

function sanitize(name) {
  return String(name || "photo")
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export async function uploadBuildingPhoto({
  buildingId,
  file,
  currentUser,
  onProgress,
}) {
  const timestamp = Date.now();
  const fileName = `${timestamp}_${sanitize(file.name)}`;
  const storagePath = `buildingPhotos/${buildingId}/${fileName}`;
  const fileRef = ref(storage, storagePath);
  const task = uploadBytesResumable(fileRef, file);

  const url = await new Promise((resolve, reject) => {
    task.on(
      "state_changed",
      (snap) => onProgress?.(snap.bytesTransferred / snap.totalBytes),
      reject,
      async () => {
        try {
          resolve(await getDownloadURL(task.snapshot.ref));
        } catch (err) {
          reject(err);
        }
      },
    );
  });

  // Match Raven's photo-doc shape so its gallery tab renders the same
  // photo without a schema translation layer. Photo id follows Raven's
  // convention (buildingId_timestamp_index).
  const photoId = `${buildingId}_${timestamp}_0`;
  const uploadedBy =
    currentUser?.email || currentUser?.displayName || null;
  const photoData = {
    url,
    fileName: file.name,
    fileSize: file.size,
    fileType: file.type,
    buildingId,
    uploadedOn: new Date().toLocaleString(),
    uploadedBy,
    createdAt: {
      seconds: Math.floor(timestamp / 1000),
      nanoseconds: 0,
    },
    // Caption/description aren't collected in the CMS's compact widget
    // — leave them empty so Raven can add them if needed. Blank is a
    // valid state in Raven too.
    caption: "",
    description: "",
    // The CMS treats the masterlist doc's `primaryPhotoUrl` as the
    // authoritative primary marker (the public site reads only that).
    // Not stamping `primary` on the photo doc keeps Raven's gallery
    // free to designate its own primary independently — the two markers
    // remain compatible.
  };
  await setDoc(doc(db, "buildingPhotos", photoId), photoData);

  return { url, path: storagePath, photoId };
}
